import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import TiktokOpsPager, { TIKTOK_OPS_DEFAULT_PAGE_SIZE } from '@/tiktok-platform/components/TiktokOpsPager';
import TiktokOpsListToolbar, { downloadCsv } from '@/tiktok-platform/components/TiktokOpsListToolbar';
import TiktokSyncProgressSlot from '@/tiktok-platform/components/TiktokSyncProgressSlot';
import { ProductFilterCard, ProductListCard } from '@/tiktok-platform/modules/products/components/ProductPageCards';
import { useTiktokSyncJobs } from '@/tiktok-platform/context/TiktokSyncJobContext';
import { useGuardedRefresh } from '@/tiktok-platform/hooks/useGuardedRefresh';
import { useTiktokPageShopScope } from '@/tiktok-platform/hooks/useTiktokPageShopScope';
import { cellDisplayTitle } from '@/tiktok-platform/utils/cellDisplayTitle';
import { buildOpsExportMenu, opsExportLabel } from '@/tiktok-platform/utils/buildOpsExportMenu';
import { togglePageSelection } from '@/tiktok-platform/utils/tiktokPageSelection';
import { formatSiteListSyncedAt } from '@/tiktok-platform/utils/tiktokSiteList';
import { tiktokPlatformAPI } from '@/utils/api';
import { showToast } from '@/utils/toast';
import '@/styles/tiktok-platform/tiktok-product-ops.css';

/** Get Brands 单页上限 100 */
const BRAND_PAGE_SIZE_OPTIONS = [30, 50, 100];
/** 导出筛选翻页上限（page_size=100 时约 5000 条；避免打爆品牌接口限流） */
const BRAND_EXPORT_MAX_PAGES = 50;
const BRAND_EXPORT_PAGE_SIZE = 100;
const COL_SPAN = 7;
const BRAND_SYNC_KINDS = ['brands'];

function unwrapBrandPayload(res) {
  if (!res || typeof res !== 'object') return { brands: [], nextPageToken: '', total: 0 };
  const data = res.data && typeof res.data === 'object' ? res.data : res;
  const brands = Array.isArray(data.brands)
    ? data.brands
    : (Array.isArray(data.items) ? data.items : []);
  return {
    brands,
    nextPageToken: String(data.next_page_token || data.nextPageToken || '').trim(),
    total: Number(data.total_count ?? data.total ?? brands.length) || brands.length,
  };
}

function brandRowKey(row) {
  return String(row?.id || row?.brand_id || row?.name || row?.brand_name || '').trim();
}

function authLabel(status) {
  const s = String(status || '').toUpperCase();
  if (s === 'AUTHORIZED') return i18n.t('brandLib.authOk', { ns: 'workbench' });
  if (s === 'UNAUTHORIEZD' || s === 'UNAUTHORIZED') return i18n.t('brandLib.authNo', { ns: 'workbench' });
  return status || '—';
}

function authTagClass(status) {
  const s = String(status || '').toUpperCase();
  if (s === 'AUTHORIZED') return 'tko-tag tko-tag--ok';
  if (s === 'UNAUTHORIEZD' || s === 'UNAUTHORIZED') return 'tko-tag tko-tag--pending';
  return 'tko-tag';
}

function brandStatusLabel(status) {
  const s = String(status || '').toUpperCase();
  if (s === 'AVAILABLE') return i18n.t('brandLib.available', { ns: 'workbench' });
  if (s === 'UNAVAILABLE') return i18n.t('brandLib.unavailable', { ns: 'workbench' });
  return status || '—';
}

function brandStatusTagClass(status) {
  const s = String(status || '').toUpperCase();
  if (s === 'AVAILABLE') return 'tko-tag tko-tag--ok';
  if (s === 'UNAVAILABLE') return 'tko-tag tko-tag--fail';
  return 'tko-tag';
}

function t1Label(value) {
  if (value === true) return i18n.t('returnDetail.yes', { ns: 'workbench' });
  if (value === false) return i18n.t('returnDetail.no', { ns: 'workbench' });
  return '—';
}

function BrandCreateModal({ open, busy, onClose, onSubmit }) {
  const { t } = useTranslation('workbench');
  const [name, setName] = useState('');
  useEffect(() => {
    if (open) setName('');
  }, [open]);
  if (!open) return null;
  return (
    <div className="tko-batch-modal" role="dialog" aria-modal="true" aria-labelledby="tko-brand-create-title">
      <button type="button" className="tko-batch-modal__backdrop" aria-label={t('moduleShell.close')} onClick={onClose} />
      <div className="tko-batch-modal__panel">
        <header className="tko-batch-modal__head">
          <h2 id="tko-brand-create-title">{t('brandLib.createTitle')}</h2>
          <button type="button" className="tko-batch-modal__close" onClick={onClose} aria-label={t('moduleShell.close')}>×</button>
        </header>
        <div className="tko-batch-modal__body">
          <p className="tiktok-ops-muted" style={{ marginTop: 0, lineHeight: 1.6 }}>
            {t('brandLib.createHint')}
          </p>
          <label className="tko-field">
            <span className="tko-field__label">{t('brandLib.colBrandName')}</span>
            <input
              className="tko-input"
              value={name}
              maxLength={30}
              placeholder={t('brandLib.nameExample')}
              disabled={busy}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && onSubmit(name)}
            />
          </label>
        </div>
        <footer className="tko-batch-modal__foot">
          <button type="button" className="tko-btn tko-btn--secondary" disabled={busy} onClick={onClose}>{t('shop.cancel')}</button>
          <button
            type="button"
            className="tko-btn tko-btn--primary"
            disabled={busy || !String(name || '').trim()}
            onClick={() => onSubmit(name)}
          >
            {busy ? t('brandLib.creating') : t('shop.create')}
          </button>
        </footer>
      </div>
    </div>
  );
}

/**
 * 模板库 · 品牌库：进页读本地缓存；「同步更新」打平台 Get Brands 写入本地。
 */
export default function BrandLibraryPage() {
  const { t } = useTranslation('workbench');
  const { concreteScopeId, listReady, shopDimensionTabs } = useTiktokPageShopScope();
  const shopId = concreteScopeId;
  const { runBlocking, isBusy } = useTiktokSyncJobs();

  const [keyword, setKeyword] = useState('');
  const [appliedKeyword, setAppliedKeyword] = useState('');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(TIKTOK_OPS_DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const selectedRowsRef = useRef(new Map());
  const [queryNonce, setQueryNonce] = useState(0);

  const loadLocal = useCallback(async () => {
    if (!shopId) {
      setItems([]);
      setTotal(0);
      setLastSyncedAt('');
      return false;
    }
    setLoading(true);
    try {
      const res = await tiktokPlatformAPI.getShopBrands(shopId, {
        page,
        pageSize,
        brandName: appliedKeyword || undefined,
      });
      const payload = unwrapBrandPayload(res);
      setItems(payload.brands);
      setTotal(payload.total);
      const synced = res?.syncedAt || res?.data?.syncedAt || '';
      if (synced) setLastSyncedAt(String(synced));
      return true;
    } catch (e) {
      setItems([]);
      setTotal(0);
      showToast(e?.message || t('brandLib.loadFailed'), 'error');
      return false;
    } finally {
      setLoading(false);
    }
  }, [shopId, page, pageSize, appliedKeyword, queryNonce, t]);

  useEffect(() => {
    setPage(1);
    setAppliedKeyword('');
    setKeyword('');
    setSelectedKeys(new Set());
    selectedRowsRef.current = new Map();
    setQueryNonce(0);
  }, [shopId]);

  // 进页默认拉本地缓存；查询/重置通过 queryNonce 强制刷新
  useEffect(() => {
    void loadLocal();
  }, [loadLocal]);

  const refreshAction = useCallback(async () => {
    if (!shopId) {
      showToast(t('brandLib.pickTiktokShop'), 'warning');
      return;
    }
    setSelectedKeys(new Set());
    selectedRowsRef.current = new Map();
    try {
      const syncRes = await runBlocking({
        kind: 'brands',
        shopId,
        label: t('brandLib.syncLabel'),
        syncFn: () => tiktokPlatformAPI.syncShopBrands(shopId),
        buildSuccess: (res) => {
          const count = Number(res?.count);
          return {
            imported: Number.isFinite(count) ? count : 0,
            message: Number.isFinite(count)
              ? t('brandLib.syncedCount', { count })
              : t('brandLib.synced'),
            silent: true,
          };
        },
      });
      if (syncRes?.syncedAt) setLastSyncedAt(String(syncRes.syncedAt));
      else setLastSyncedAt(new Date().toISOString());
      setPage(1);
      setAppliedKeyword(String(keyword || '').trim());
      setQueryNonce((n) => n + 1);
      const count = Number(syncRes?.count);
      showToast(
        Number.isFinite(count) ? t('brandLib.syncedCount', { count }) : t('brandLib.synced'),
        'success',
      );
    } catch (e) {
      if (e?.code === 'SYNC_IN_PROGRESS' || e?.code === 'ABORT') return;
      showToast(e?.message || t('brandLib.syncFailed'), 'error');
    }
  }, [shopId, keyword, runBlocking, t]);

  const {
    refresh: onSync,
    refreshing: syncingLocal,
    cooldownSeconds: syncCooldown,
  } = useGuardedRefresh(refreshAction);

  const syncing = syncingLocal || Boolean(shopId && isBusy('brands', shopId));

  const latestSyncedLabel = lastSyncedAt
    ? t('tiktokCatalog.updatedAt', { time: formatSiteListSyncedAt(lastSyncedAt) })
    : t('tiktokCatalog.notSynced');

  const rows = useMemo(() => (Array.isArray(items) ? items : []), [items]);
  const selectableKeys = useMemo(
    () => rows.map((row) => brandRowKey(row)).filter(Boolean),
    [rows],
  );
  const allPageSelected = selectableKeys.length > 0
    && selectableKeys.every((key) => selectedKeys.has(key));

  const toggleAll = () => {
    if (!selectableKeys.length) return;
    setSelectedKeys((prev) => {
      const next = togglePageSelection(prev, selectableKeys, allPageSelected);
      if (allPageSelected) {
        selectableKeys.forEach((key) => selectedRowsRef.current.delete(key));
      } else {
        rows.forEach((row) => {
          const key = brandRowKey(row);
          if (key) selectedRowsRef.current.set(key, row);
        });
      }
      return next;
    });
  };

  const toggleOne = (row) => {
    const key = brandRowKey(row);
    if (!key) return;
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        selectedRowsRef.current.delete(key);
      } else {
        next.add(key);
        selectedRowsRef.current.set(key, row);
      }
      return next;
    });
  };

  const resolveSelectedRows = () => (
    [...selectedKeys].map((key) => (
      selectedRowsRef.current.get(key)
      || rows.find((row) => brandRowKey(row) === key)
    )).filter(Boolean)
  );

  const mapRowsToCsv = (list) => {
    const headers = [t('brandLib.colBrandId'), t('brandLib.colBrandName'), t('brandLib.colAuth'), t('brandLib.colT1'), t('brandLib.colAvailable')];
    const mapped = list.map((row) => ({
      [t('brandLib.colBrandId')]: row.id || row.brand_id || '',
      [t('brandLib.colBrandName')]: row.name || row.brand_name || '',
      [t('brandLib.colAuth')]: authLabel(row.authorized_status),
      [t('brandLib.colT1')]: t1Label(row.is_t1_brand),
      [t('brandLib.colAvailable')]: brandStatusLabel(row.brand_status),
    }));
    return { headers, mapped };
  };

  const exportCsvRows = (list) => {
    if (!list.length) {
      showToast(t('brandLib.nothingToExport'), 'warning');
      return;
    }
    const { headers, mapped } = mapRowsToCsv(list);
    downloadCsv(`tiktok-brands-${Date.now()}.csv`, headers, mapped);
    showToast(t('tiktokCatalog.exported', { count: list.length }), 'success');
  };

  const fetchFilteredBrands = useCallback(async () => {
    if (!shopId) return { rows: [], truncated: false };
    const all = [];
    for (let pageNo = 1; pageNo <= BRAND_EXPORT_MAX_PAGES; pageNo += 1) {
      // eslint-disable-next-line no-await-in-loop
      const res = await tiktokPlatformAPI.getShopBrands(shopId, {
        page: pageNo,
        pageSize: BRAND_EXPORT_PAGE_SIZE,
        brandName: appliedKeyword || undefined,
      });
      const payload = unwrapBrandPayload(res);
      all.push(...payload.brands);
      if (!payload.brands.length || all.length >= payload.total) {
        return { rows: all, truncated: false };
      }
      if (pageNo === BRAND_EXPORT_MAX_PAGES && all.length < payload.total) {
        return { rows: all, truncated: true };
      }
    }
    return { rows: all, truncated: false };
  }, [shopId, appliedKeyword]);

  const onExportCsvFiltered = async () => {
    if (!shopId) {
      showToast(t('brandLib.pickTiktokShop'), 'warning');
      return;
    }
    if (exporting) return;
    setExporting(true);
    try {
      const { rows: list, truncated } = await fetchFilteredBrands();
      if (!list.length) {
        showToast(t('brandLib.nothingToExport'), 'warning');
        return;
      }
      const { headers, mapped } = mapRowsToCsv(list);
      downloadCsv(`tiktok-brands-${Date.now()}.csv`, headers, mapped);
      showToast(
        truncated
          ? t('brandLib.exportTruncated', { count: list.length, max: BRAND_EXPORT_MAX_PAGES * BRAND_EXPORT_PAGE_SIZE })
          : t('tiktokCatalog.exported', { count: list.length }),
        truncated ? 'warning' : 'success',
      );
    } catch (e) {
      showToast(e?.message || t('tiktokCatalog.exportFilterFailed'), 'error');
    } finally {
      setExporting(false);
    }
  };

  const onExportCsvPage = () => {
    exportCsvRows(rows);
  };

  const onExportCsvSelected = () => {
    const list = resolveSelectedRows();
    if (!list.length) {
      showToast(t('brandLib.pickExport'), 'warning');
      return;
    }
    exportCsvRows(list);
  };

  const onCopyBrandId = async (row) => {
    const id = String(row?.id || row?.brand_id || '').trim();
    if (!id) {
      showToast(t('brandLib.noBrandId'), 'warning');
      return;
    }
    try {
      await navigator.clipboard.writeText(id);
      showToast(t('brandLib.copiedId'), 'success');
    } catch {
      showToast(t('brandLib.copyFailed'), 'error');
    }
  };

  const onQuery = () => {
    if (!shopId) {
      showToast(t('brandLib.pickTiktokShop'), 'warning');
      return;
    }
    setAppliedKeyword(String(keyword || '').trim());
    setPage(1);
    setQueryNonce((n) => n + 1);
  };

  const onReset = () => {
    setKeyword('');
    setAppliedKeyword('');
    setPage(1);
    setQueryNonce((n) => n + 1);
  };

  const onCreate = async (rawName) => {
    const name = String(rawName || '').trim();
    if (!shopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    if (name.length < 2 || name.length > 30) {
      showToast(t('brandLib.nameLen'), 'warning');
      return;
    }
    if (/[\u4e00-\u9fff]/.test(name)) {
      showToast(t('brandLib.nameNoZh'), 'warning');
      return;
    }
    if (/\d{5,}/.test(name)) {
      showToast(t('brandLib.nameNoDigits'), 'warning');
      return;
    }
    setCreating(true);
    try {
      const res = await tiktokPlatformAPI.createShopBrand(shopId, { name });
      const createdId = String(res?.id || res?.brand_id || res?.item?.id || '').trim();
      showToast(
        createdId ? t('brandLib.createdWithId', { name, id: createdId }) : t('brandLib.created', { name }),
        'success',
      );
      setCreateOpen(false);
      setKeyword(name);
      setAppliedKeyword(name);
      setPage(1);
      setQueryNonce((n) => n + 1);
      await loadLocal();
    } catch (e) {
      showToast(e?.message || t('brandLib.createFailed'), 'error');
    } finally {
      setCreating(false);
    }
  };

  const emptyTitle = !listReady || !shopId
    ? t('returnList.pickShop')
    : loading
      ? t('returnList.loading')
      : (appliedKeyword ? t('brandLib.noMatch') : t('brandLib.emptyLocal'));

  const emptyHint = !listReady || !shopId
    ? t('brandLib.emptyHintPick')
    : loading
      ? ''
      : (appliedKeyword
        ? t('brandLib.emptyHintFilter')
        : t('brandLib.emptyHintPull'));

  const busy = loading || syncing || creating || exporting;
  const showEmpty = !listReady || !shopId || loading || rows.length === 0;

  return (
    <>
      <TiktokSyncProgressSlot kinds={BRAND_SYNC_KINDS} />
      <ProductFilterCard
        toolbar={(
          <TiktokOpsListToolbar
            selectedCount={selectedKeys.size}
            busy={busy}
            syncKinds={BRAND_SYNC_KINDS}
            onSync={() => {
              if (!shopId) {
                showToast(t('brandLib.pickTiktokShop'), 'warning');
                return;
              }
              onSync();
            }}
            syncing={syncing}
            syncCooldownSeconds={syncCooldown}
            syncDisabled={!shopId || syncing || exporting}
            lastSyncedLabel={latestSyncedLabel}
            syncHint={t('brandLib.syncHint')}
            exportLabel={opsExportLabel(exporting)}
            exportDisabled={!shopId || exporting}
            exportMenu={buildOpsExportMenu({
              exporting,
              filteredCount: total,
              pageCount: rows.length,
              selectedCount: selectedKeys.size,
              onExportFiltered: onExportCsvFiltered,
              onExportPage: onExportCsvPage,
              onExportSelected: onExportCsvSelected,
            })}
            extraActions={[
              {
                key: 'create-brand',
                label: t('brandLib.create'),
                disabled: !shopId || creating || exporting,
                onClick: () => setCreateOpen(true),
              },
            ]}
          />
        )}
      >
        <div className="tko-filter-bar">
          <div className="tko-filter-bar__fields">
            <div className="tko-field tko-field--inline tko-field--w300">
              <span className="tko-field__label">{t('brandLib.brandNameFilter')}</span>
              <input
                className="tko-input"
                value={keyword}
                placeholder={t('brandLib.prefixPh')}
                disabled={!shopId}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
            </div>
          </div>
          <div className="tko-filter-bar__actions">
            <button
              type="button"
              className="tko-btn tko-btn--secondary tko-btn--sm"
              disabled={!shopId || loading}
              onClick={onReset}
            >
              {t('returnList.reset')}
            </button>
            <button
              type="button"
              className="tko-btn tko-btn--primary tko-btn--sm"
              disabled={!shopId || loading}
              onClick={onQuery}
            >
              {t('returnList.query')}
            </button>
          </div>
        </div>
      </ProductFilterCard>

      <ProductListCard tabs={shopDimensionTabs} loading={loading} loadingLabel={t('brandLib.loadingBrands')}>
        <div className="tko-drafts__table-wrap tko-ops-table-wrap">
          <table className="tko-publish__table tko-drafts__table tko-ops-table tko-ops-table--sticky-head tko-ops-table--brands">
            <colgroup>
              <col className="tko-ops-col--check" />
              <col className="tko-ops-col--brand-id" />
              <col className="tko-ops-col--brand-name" />
              <col className="tko-ops-col--brand-auth" />
              <col className="tko-ops-col--brand-t1" />
              <col className="tko-ops-col--brand-status" />
              <col className="tko-ops-col--ops" />
            </colgroup>
            <thead>
              <tr>
                <th
                  scope="col"
                  className="tko-ops-th tko-ops-th--check"
                  onClick={() => {
                    if (!shopId || loading || selectableKeys.length === 0) return;
                    toggleAll();
                  }}
                >
                  <input
                    type="checkbox"
                    aria-label={t('productTemplate.selectAll')}
                    disabled={!shopId || loading || selectableKeys.length === 0}
                    checked={allPageSelected}
                    onChange={toggleAll}
                    onClick={(e) => e.stopPropagation()}
                  />
                </th>
                <th scope="col">{t('brandLib.colBrandId')}</th>
                <th scope="col">{t('brandLib.colBrandName')}</th>
                <th scope="col">{t('brandLib.colAuth')}</th>
                <th scope="col">{t('brandLib.colT1')}</th>
                <th scope="col">{t('brandLib.colAvailable')}</th>
                <th scope="col" className="tko-ops-th tko-ops-th--ops">{t('returnList.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {showEmpty ? (
                <tr>
                  <td colSpan={COL_SPAN} className="tko-certs-empty-cell">
                    <div className="tko-certs-empty">
                      <p className="tko-certs-empty__title">{emptyTitle}</p>
                      {emptyHint ? <p className="tko-certs-empty__desc">{emptyHint}</p> : null}
                    </div>
                  </td>
                </tr>
              ) : rows.map((row) => {
                const key = brandRowKey(row);
                const id = row.id || row.brand_id || '';
                const name = row.name || row.brand_name || '';
                const checked = selectedKeys.has(key);
                return (
                  <tr
                    key={key || String(id || name)}
                    className={`tko-row--clickable${checked ? ' is-selected' : ''}`}
                    onClick={(e) => {
                      if (e.target.closest('button, a, input, .tiktok-draft-actions')) return;
                      toggleOne(row);
                    }}
                  >
                    <td
                      className="tko-ops-td tko-ops-td--check"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleOne(row);
                      }}
                    >
                      <input
                        type="checkbox"
                        aria-label={t('returnList.selectRow', { title: name || id || key })}
                        checked={checked}
                        onChange={() => toggleOne(row)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td className="tko-ops-td--brand-id" title={cellDisplayTitle(id)}>
                      {id || '—'}
                    </td>
                    <td title={cellDisplayTitle(name)}>{name || '—'}</td>
                    <td>
                      <span className={authTagClass(row.authorized_status)}>
                        {authLabel(row.authorized_status)}
                      </span>
                    </td>
                    <td>{t1Label(row.is_t1_brand)}</td>
                    <td>
                      <span className={brandStatusTagClass(row.brand_status)}>
                        {brandStatusLabel(row.brand_status)}
                      </span>
                    </td>
                    <td className="tko-ops-td tko-ops-td--ops tko-certs-td--ops">
                      <div className="tiktok-draft-actions">
                        <button
                          type="button"
                          className="wf-tasks-actions__link"
                          disabled={!id}
                          title={t('brandLib.copyIdTitle')}
                          onClick={(e) => {
                            e.stopPropagation();
                            void onCopyBrandId(row);
                          }}
                        >
                          {t('brandLib.copyId')}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <TiktokOpsPager
          page={page}
          pageSize={pageSize}
          total={total}
          disabled={loading}
          pageSizeOptions={BRAND_PAGE_SIZE_OPTIONS}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          ariaLabel={t('brandLib.pager')}
        />
      </ProductListCard>

      <BrandCreateModal
        open={createOpen}
        busy={creating}
        onClose={() => !creating && setCreateOpen(false)}
        onSubmit={onCreate}
      />
    </>
  );
}
